/**
 * Mutators (Tier 3, Feature H) — rule-variant CATEGORIES over any level.
 *
 * A mutator changes how the sim PLAYS without touching the level's content. The
 * engine was already built for this: a Form is "just a Partial<PhysicsConfig> merged
 * over a base via mergeConfig" — a config mutator is the SAME shape (another overlay),
 * and the behavioural mutators (mirror / one-life / hardcore) are a few flags read in
 * World. Two kinds:
 *
 *   1. CONFIG overlays — multiply selected PhysicsConfig fields of the RESOLVED config
 *      (so low-grav on any archetype still feels like that archetype, just lighter). Pure float multiply
 *      → IEEE-754 bit-stable, so they obey the engine's no-transcendentals lint and
 *      slot into the deterministic fixed step unchanged.
 *   2. BEHAVIOURAL flags — `mirror` (negate axisX before player.update), `one-life`
 *      (a death ends the run instead of respawning), `hardcore` (zero i-frames).
 *
 * THE design constraint (see docs/HANDOFF_TIER3_REACH.md): a mutated run is a DIFFERENT
 * challenge than vanilla — a speedrun.com-style category. So a Replay records the active
 * set and leaderboards key on (levelId, mutatorSetId); vanilla (mutatorSetId === 0) is
 * the default board. This module is the registry + the canonical `mutatorSetId` hash.
 *
 * Lives in src/engine/ and is therefore bound by the determinism lint: integer-only id
 * hashing (the same FNV-1a pattern as computeLevelId), no Math.random / Date.now /
 * transcendentals; the only floats are the config multipliers (exact under IEEE-754).
 */
import { mergeConfig, type PhysicsConfig } from './PhysicsConfig';

/** Every mutator id. The ORDER here is the canonical UI order (the picker renders it). */
export const MUTATOR_IDS = [
  'low-grav',
  'moon-jump',
  'ice',
  'turbo',
  'mirror',
  'one-life',
  'hardcore',
] as const;

export type MutatorId = (typeof MUTATOR_IDS)[number];

/** PhysicsConfig fields that are NUMBERS — the only fields a config mutator may scale (the
 *  config now also carries boolean flags like wallJumpAdditive, which a multiplier can't touch). */
type NumericConfigKey = {
  [K in keyof PhysicsConfig]: PhysicsConfig[K] extends number ? K : never;
}[keyof PhysicsConfig];

/** A config mutator: multiply selected PhysicsConfig fields of the resolved form. */
interface ConfigMutatorDef {
  id: MutatorId;
  label: string;
  blurb: string;
  kind: 'config';
  /** field → multiplier applied to the resolved config (absent fields untouched). */
  mult: Partial<Record<NumericConfigKey, number>>;
}

/** A behavioural mutator: a flag read directly in World (not expressible as config). */
interface FlagMutatorDef {
  id: MutatorId;
  label: string;
  blurb: string;
  kind: 'flag';
}

export type MutatorDef = ConfigMutatorDef | FlagMutatorDef;

/**
 * The registry. Config multipliers follow the brief's spec (low-grav = 0.6× gravity,
 * etc); applied multiplicatively over the resolved form so form character is preserved.
 */
export const MUTATORS: Record<MutatorId, MutatorDef> = {
  'low-grav': {
    id: 'low-grav', label: 'Low-grav', kind: 'config',
    blurb: 'Floaty — gravity ×0.6',
    mult: { gravity: 0.6, jumpReleaseGravity: 0.6 },
  },
  'moon-jump': {
    id: 'moon-jump', label: 'Moon-jump', kind: 'config',
    blurb: 'Springy legs — jump ×1.3',
    mult: { jumpVelocity: 1.3 },
  },
  ice: {
    id: 'ice', label: 'Ice', kind: 'config',
    blurb: 'Slippery — much less ground friction',
    mult: { groundDecel: 0.25, skidDecel: 0.4 },
  },
  turbo: {
    id: 'turbo', label: 'Turbo', kind: 'config',
    blurb: 'Faster top speed + accel ×1.4',
    mult: { walkMaxSpeed: 1.4, runMaxSpeed: 1.4, groundAccel: 1.4 },
  },
  mirror: {
    id: 'mirror', label: 'Mirror', kind: 'flag',
    blurb: 'Left is right — controls flipped',
  },
  'one-life': {
    id: 'one-life', label: 'One-life', kind: 'flag',
    blurb: 'No respawns — one death ends the run',
  },
  hardcore: {
    id: 'hardcore', label: 'Hardcore', kind: 'flag',
    blurb: 'No i-frames — every hit lands',
  },
};

/** A frozen, validated, SORTED, de-duplicated mutator set + its derived behaviour. */
export interface ActiveMutators {
  /** Canonical sorted+deduped active ids (the source of truth for the set). */
  readonly ids: readonly MutatorId[];
  /** Integer category key — 0 = vanilla. Leaderboards key on (levelId, this). */
  readonly setId: number;
  /** behavioural flags, pre-resolved for O(1) reads in the hot path. */
  readonly mirror: boolean;
  readonly oneLife: boolean;
  readonly hardcore: boolean;
  /** Apply every active CONFIG overlay to a resolved form config (pure, deterministic). */
  applyOverlay(cfg: PhysicsConfig): PhysicsConfig;
  /** Human-readable category, e.g. "Low-grav · One-life" (or "Vanilla" when empty). */
  label(): string;
}

/** Keep only KNOWN ids, de-dupe, and sort into the canonical order (UI + hash order). */
export function sanitizeMutators(raw: Iterable<unknown>): MutatorId[] {
  const seen = new Set<MutatorId>();
  for (const v of raw) {
    if (typeof v === 'string' && (MUTATOR_IDS as readonly string[]).includes(v)) {
      seen.add(v as MutatorId);
    }
  }
  return MUTATOR_IDS.filter((id) => seen.has(id));
}

/**
 * Canonical integer category id for a mutator set — the SAME integer FNV-1a pattern as
 * computeLevelId (LevelFormat.ts), so it obeys the no-transcendentals lint and is
 * bit-stable cross-engine. The empty set is 0 (= vanilla); the hash is taken over the
 * sorted active keys joined by ',' so it is invariant under selection order. A non-empty
 * set that hashed to 0 is bumped off the vanilla sentinel (astronomically unlikely).
 */
export function mutatorSetId(raw: Iterable<unknown>): number {
  const ids = sanitizeMutators(raw);
  if (ids.length === 0) return 0;
  const str = ids.join(',');
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const out = h >>> 0;
  return out === 0 ? 0x9e3779b1 : out; // never collide with the vanilla sentinel
}

/** Resolve a raw selection into a validated, behaviour-derived ActiveMutators. */
export function resolveMutators(raw: Iterable<unknown>): ActiveMutators {
  const ids = sanitizeMutators(raw);
  const setId = mutatorSetId(ids);
  const configMults = ids
    .map((id) => MUTATORS[id])
    .filter((d): d is ConfigMutatorDef => d.kind === 'config');
  return {
    ids,
    setId,
    mirror: ids.includes('mirror'),
    oneLife: ids.includes('one-life'),
    hardcore: ids.includes('hardcore'),
    applyOverlay(cfg: PhysicsConfig): PhysicsConfig {
      // Fold each active overlay over the running config (sorted order → deterministic
      // even if two overlays touched the same field). Pure multiply; original untouched.
      let out = cfg;
      for (const def of configMults) {
        const override: Partial<PhysicsConfig> = {};
        for (const key of Object.keys(def.mult) as NumericConfigKey[]) {
          override[key] = out[key] * (def.mult[key] as number);
        }
        out = mergeConfig(out, override);
      }
      return out;
    },
    label(): string {
      if (ids.length === 0) return 'Vanilla';
      return ids.map((id) => MUTATORS[id].label).join(' · ');
    },
  };
}

/** Vanilla (no mutators) — the default category every level ships with. */
export const NO_MUTATORS: ActiveMutators = resolveMutators([]);

/** Parse a comma-separated mutator string (URL `?m=` / play-host injection) → ids. */
export function parseMutatorParam(param: string | null | undefined): MutatorId[] {
  if (!param) return [];
  return sanitizeMutators(param.split(','));
}

/** Serialize a set back to the canonical comma string for a `?m=` deep link. */
export function mutatorParam(ids: Iterable<MutatorId>): string {
  return sanitizeMutators(ids).join(',');
}
